import { ChargeEvent, DisplayMeasurementProfileDetail } from "@fairfleet/geotab";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Modal from "@mui/material/Modal";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { GridCellParams } from "@mui/x-data-grid-pro";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ChargeEventDetails from "./ChargeEventDetails";
import { LastNhDiagnosticLineChart } from "./LastNhDiagnosticLineChart";
import RangeEstimateChart from "./RangeEstimateChart";
import { useDataGridSettings } from "@/components/DataGridSettingsProvider";

const RANGE_DIAGNOSTIC_ID = "aFGR6Q9guoUeiaAZI1fD6oQ";

// Display the graph of the last 24 hours of a diagnostic
// if its chargeRate, also display the charge events
const DetailsModal: React.FC<{
  open: boolean;
  handleClose: () => void;
  modalData: GridCellParams | null;
}> = ({ open, handleClose, modalData }) => {
  const { t } = useTranslation();
  const { columnNames, diagnosticUnits } = useDataGridSettings();
  const [tab, setTab] = useState(0);

  useEffect(() => {
    setTab(0);
  }, [modalData]);

  if (!modalData) return <></>;
  const field = modalData?.field ?? "";
  const headerName = modalData?.colDef?.headerName ?? "";

  const chargeEventDetail = field === "chargeRate";
  const ignitionVoltageDetail = field === "ignitionStatus";
  const isRangeDiagnostic = field === RANGE_DIAGNOSTIC_ID;

  const deviceId = modalData?.row.device.id as string;
  const deviceName = modalData?.row.device?.name ?? "";
  const chargeEvents = (modalData?.row.chargeEvents ?? []) as ChargeEvent[];
  const diagnosticId = ignitionVoltageDetail
    ? "DiagnosticCrankingVoltageId"
    : chargeEventDetail
      ? "DiagnosticElectricVehicleBatteryPowerId"
      : (field ?? "");
  const title = ignitionVoltageDetail
    ? t("modal.ignitionVoltageDetails")
    : chargeEventDetail
      ? t("modal.chargeDetails")
      : (columnNames?.[field] ?? headerName ?? "");

  const unit = chargeEventDetail
    ? ({
        displayUnitOfMeasure: { name: "kW", factorFromBase: 0.001 },
      } as unknown as DisplayMeasurementProfileDetail)
    : diagnosticUnits?.find(du => du.diagnosticId == diagnosticId);

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "60%",
          bgcolor: "background.paper",
          boxShadow: 24,
          borderRadius: 8,
          p: 4,
          maxHeight: "80vh",
          overflowY: "auto",
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6">
              {tab === 0
                ? `${title}${
                    isRangeDiagnostic
                      ? ""
                      : `  (${t("modal.last")} ${
                          ignitionVoltageDetail
                            ? "14 " + t("faultOverview.days")
                            : "24 " + t("faultOverview.hours")
                        })`
                  }`
                : t("modal.rangeEstimate.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {deviceName}
            </Typography>
          </Box>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {isRangeDiagnostic && (
          <Tabs value={tab} onChange={(_e, v: number) => setTab(v)} sx={{ mt: 1, mb: 2 }}>
            <Tab label={`${title} (24 ${t("faultOverview.hours")})`} />
            <Tab label={t("modal.rangeEstimate.title")} sx={{ whiteSpace: "nowrap" }} />
          </Tabs>
        )}

        {tab === 0 && (
          <>
            <Box sx={{ width: "100%", height: "60vh", mx: "auto" }}>
              <LastNhDiagnosticLineChart
                deviceId={deviceId}
                diagnosticId={diagnosticId}
                diagnosticUnit={unit}
                hours={ignitionVoltageDetail ? 24 * 14 : undefined}
              />
            </Box>
            <ChargeEventDetails chargeEvents={chargeEventDetail ? chargeEvents : []} />
          </>
        )}

        {tab === 1 && isRangeDiagnostic && (
          <RangeEstimateChart deviceId={deviceId} />
        )}
      </Box>
    </Modal>
  );
};

export default DetailsModal;
